import { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { AuthorOption } from "../types/General";
import { SearchUsersForAutocomplete } from "../services/home";

const USER_PLACEHOLDER = "/static/images/user_placeholder.png";

const getDisplayName = (option: AuthorOption): string => {
  const fullName = [option.firstName, option.lastName].filter(Boolean).join(" ");
  return fullName || option.username || option.email;
};

const getProfileImageUrl = (option: AuthorOption): string => {
  return option.profileImageThumbnail || option.profileImage || USER_PLACEHOLDER;
};

interface AuthorSelectProps {
  value: AuthorOption | null;
  onChange: (value: AuthorOption | null) => void;
}

const AuthorSelect = ({ value, onChange }: AuthorSelectProps) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<AuthorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchUsers = useCallback(async (search: string) => {
    setLoading(true);
    try {
      const response = (await SearchUsersForAutocomplete({
        search,
        limit: 10,
      })) as { code: number; data: AuthorOption[] } | undefined;
      if (response?.code === 200) {
        setOptions(response.data || []);
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.log(error);
      setOptions([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchUsers(inputValue);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputValue, fetchUsers]);

  return (
    <Autocomplete
      value={value}
      onChange={(_event, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      options={options}
      getOptionLabel={(option) => getDisplayName(option)}
      isOptionEqualToValue={(option, val) => option._id === val._id}
      filterOptions={(x) => x}
      loading={loading}
      noOptionsText={inputValue ? "No users found" : "Type to search users"}
      ListboxProps={{
        sx: {
          py: 1.5,
          px: 1,
          "& .MuiAutocomplete-option": {
            padding: "8px 12px !important",
            borderRadius: "6px",
          },
        },
      }}
      renderOption={(props, option, { index }) => (
        <Box key={option._id}>
          <Box
            component="li"
            {...props}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Avatar
              src={getProfileImageUrl(option)}
              alt={getDisplayName(option)}
              imgProps={{
                onError: (e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = USER_PLACEHOLDER;
                },
              }}
              sx={{ width: 32, height: 32 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, lineHeight: 1.2 }}
                noWrap
              >
                {getDisplayName(option)}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ fontSize: 11, lineHeight: 1.2 }}
                noWrap
              >
                {option.email}
              </Typography>
            </Box>
          </Box>
          {index < options.length - 1 && (
            <Divider sx={{ my: 0.5 }} />
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Author"
          placeholder="Search by name or email..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AuthorSelect;
